import * as React from 'react';
import { Box, Divider } from '@mui/material';
import type { StackProps } from '@mui/material/Stack';
import Stack from '@mui/material/Stack';
import { Passport } from '@/types/api';
import { copyClipBoardText } from '@/utils';
import { imgUrlChk } from '@/utils/function';
import ImageWithErrorHandling from '@/components/ImageWithErrorHandling';
import ProfileContent from './ProfileContent';
import useLayoutSize from '@/hooks/useLayoutSize';
import ProfileIDCard from './ProfileIDCard';
import OpenTeamsLinkButton from '@/components/@IntranetLibrary/Button/OpenTeamsLinkButton';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@/hooks/global-tools';
import { WORKING_HOURS_OPTIONS } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkingHoursChange/@hooks/useWorkingHoursChangeProps';

export enum ProfileCardViewType {
  SIMPLE = 'ProfileCardViewType-SIMPLE',
  BADGE = 'ProfileCardViewType-BADGE',
}

export type ProfileCardProps = {
  isLoading: boolean;
  data?: Passport;
  view?: ProfileCardViewType;
} & StackProps;

const ProfileCard = (_props: ProfileCardProps) => {
  const {
    isLoading,
    data,
    view = ProfileCardViewType.SIMPLE,
    sx,
    ...props
  } = _props;
  const { isMobile } = useLayoutSize();
  const { t } = useTranslation();
  const toast = useSnackbar();

  const copyEmail = async () => {
    if (!data?.detail.email) return;
    const resultCopy = await copyClipBoardText(data.detail.email);
    if (resultCopy) {
      toast.show({
        severity: 'success',
        message: '이메일이 복사되었습니다.',
        duration: 3000,
      });
    }
  };

  if (!data) {
    return (
      <Box
        sx={{
          width:
            view === ProfileCardViewType.SIMPLE
              ? '320px'
              : isMobile
              ? '360px'
              : '688px',
          height: view === ProfileCardViewType.SIMPLE ? '394px' : '560px',
          padding: isMobile ? '20px' : '32px 24px',
          fontSize: isMobile ? '12px' : '14px',
        }}
      >
        {isLoading ? (
          t('불러오는 중...')
        ) : (
          <>
            프로필 카드가 없습니다.
            <br />
            휴직 혹은 퇴사자일 수 있습니다.
          </>
        )}
      </Box>
    );
  }

  if (view === ProfileCardViewType.SIMPLE) {
    return (
      <Stack
        direction={'column'}
        spacing={'16px'}
        sx={{ padding: '24px 16px 16px', backgroundColor: 'var(--dk-bg, #fff)', ...sx }}
        {...props}
      >
        <Stack direction={'column'} spacing={'8px'}>
          <ImageWithErrorHandling
            src={imgUrlChk(data.detail.profileImageUrl)}
            width={100}
            height={100}
            alt={'profile-image'}
            style={{
              margin: '0 auto',
              borderRadius: '4px',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              '> p': { textAlign: 'center', margin: 0 },
              '.name': { fontSize: '14px' },
              '.positionName': {
                marginTop: '4px',
                fontSize: '12px',
                color: 'var(--dk-text-secondary, #888D96)',
              },
            }}
          >
            <p className={'name'}>
              {data.detail.name}{' '}
              {data.detail.nickname && `(${data.detail.nickname})`}
            </p>
            <p className={'positionName'}>{data.detail.positionName}</p>
          </Box>
          <Box>
            <Box
              sx={{
                padding: '8px 0',
                borderTop: '1px solid var(--dk-line, #E7E9EC)',
                borderBottom: '1px solid var(--dk-line, #E7E9EC)',
                textAlign: 'center',
                fontSize: '12px',
                color: 'var(--dk-text-secondary, #888D96)',
                wordBreak: 'keep-all',
              }}
            >
              <Box>
                {data.detail.companyName} · {data.detail.divisionName} ·{' '}
                {data.detail.departmentName}
              </Box>
            </Box>
          </Box>
        </Stack>
        <Stack
          direction={'column'}
          justifyContent={'center'}
          alignItems={'center'}
          spacing={'8px'}
          sx={{
            paddingBottom: '16px',
            a: {
              fontSize: '12px',
              textDecoration: 'underline',
              cursor: 'pointer',
            },
          }}
        >
          <Stack
            direction={'row'}
            justifyContent={'center'}
            alignItems={'center'}
            divider={
              <Divider
                orientation={'vertical'}
                sx={{
                  height: '8px',
                  // #BoardDetailComment hr 에 margin 영향을 받고 있음
                  marginX: '8px !important',
                  borderColor: 'var(--dk-line-strong, #D4D7DB)',
                }}
              />
            }
          >
            {data.detail.phoneInside && (
              <a href={`tel:${data.detail.phoneInside}`}>
                {data.detail.phoneInside}
              </a>
            )}
            <a href={`tel:${data.detail.phoneMobile}`}>
              {data.detail.phoneMobile}
            </a>
          </Stack>
          {data.detail.email && (
            <a onClick={copyEmail} style={{ wordBreak: 'break-word' }}>
              {data.detail.email}
            </a>
          )}
          {data.detail.workingHours && (
            <span style={{ fontSize: '12px', color: 'var(--dk-text, black)' }}>
              {t('근무시간')} :{' '}
              {WORKING_HOURS_OPTIONS.find(
                (opt) => opt.value === data.detail.workingHours,
              )?.label ?? data.detail.workingHours}
            </span>
          )}
        </Stack>
        <OpenTeamsLinkButton email={data.detail.email} />
      </Stack>
    );
  }

  return (
    <Stack
      direction={isMobile ? 'column' : 'row'}
      justifyContent={'center'}
      alignItems={'center'}
      gap={isMobile ? '56px' : '24px'}
      sx={{ minHeight: '602px', '> div': { alignSelf: 'self-end' }, ...sx }}
      {...props}
    >
      <ProfileIDCard
        passport={data}
        sx={{ marginTop: isMobile ? '72px' : 0 }}
      />
      {!isMobile && (
        <div
          style={{
            width: '1px',
            alignSelf: 'stretch',
            backgroundColor: 'var(--dk-bg-strong, #E7E9EC)',
          }}
        />
      )}
      <ProfileContent
        passport={data}
        sx={{
          width: isMobile ? '100%' : '288px',
          paddingTop: isMobile ? 0 : '32px',
          maxHeight: isMobile ? '' : '640px',
          overflowY: isMobile ? '' : 'scroll',
        }}
      />
    </Stack>
  );
};

export default ProfileCard;
